import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Input, Pagination, Segmented, Select, message } from 'antd';
import {
  CartesianGrid,
  Cell,
  Line,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import type { TooltipProps } from 'recharts';
import {
  DollarSign,
  FileSpreadsheet,
  FileText,
  Search,
  TrendingUp,
  Users,
} from 'lucide-react';
import { PageHeader } from '@/shared/components';
import { Button } from '@/shared/components/Button';
import { KpiCard } from '@/features/budgets/components/KpiCard';
import { useBudgetReportYearsListQuery } from '@/features/budgets/hooks/useBudgetQueries';
import { useDefaultYear } from '@/features/settings/hooks/useDashboardSettingsQuery';
import {
  useAreasForCitiesQuery,
  useCitiesForGovernoratesQuery,
  useGovernoratesQuery,
} from '@/features/region/hooks/useRegionQueries';
import { useSchoolSizeCostQuery } from '@/features/reports/hooks/useSchoolSizeCostQuery';
import { useSchoolSizeCostSchoolBandsQuery } from '@/features/reports/hooks/useSchoolSizeCostSchoolBandsQuery';
import {
  SCALE_VARIANCE_THRESHOLD_PCT,
  efficiencyStatusLabel,
} from '@/features/reports/data/schoolSizeCost.constants';
import { bandFullName } from '@/features/reports/utils/bandDisplayLabels';
import type {
  SchoolSizeCostDistribution,
  SchoolSizeCostEfficiencyStatus,
  SchoolSizeCostFilterParams,
  SchoolSizeCostRowFilter,
  SchoolSizeCostSchoolRow,
  SchoolSizeCostViewMode,
} from '@/features/reports/data/schoolSizeCost.types';
import {
  exportSchoolSizeCostExcel,
  exportSchoolSizeCostPdf,
  schoolSizeCostExportBasename,
} from '@/features/reports/utils/schoolSizeCostExports';
import { schoolProfileHref } from '@/app/router/routes';

const ANY_VALUE = '';
const TABLE_PAGE_SIZE_OPTIONS = [10, 15, 25, 50] as const;
const CHART_MAX_POINTS = 80;

type SortKey =
  | 'schoolName'
  | 'studentsCount'
  | 'costPerStudentUsd'
  | 'tuitionPerStudentUsd'
  | 'varianceVsScalePct';

type SortDir = 'asc' | 'desc';

function withAnyOption(items: { value: string; label: string }[]) {
  return [{ value: ANY_VALUE, label: 'All governorates' }, ...items];
}

function fmtUsd(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

function fmtPct(n: number): string {
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

function fmtInt(n: number): string {
  return new Intl.NumberFormat('en-US').format(Math.round(n));
}

function statusBadgeClass(status: SchoolSizeCostEfficiencyStatus): string {
  if (status === 'high_cost_for_size') return 'bg-amber-100 text-amber-800 ring-amber-200/80';
  if (status === 'low_cost_for_size') return 'bg-emerald-100 text-emerald-800 ring-emerald-200/80';
  return 'bg-slate-100 text-slate-700 ring-slate-200/80';
}

function scatterFill(status: SchoolSizeCostEfficiencyStatus): string {
  if (status === 'high_cost_for_size') return '#d97706';
  if (status === 'low_cost_for_size') return '#059669';
  return '#4f46e5';
}

type ScatterChartPoint = {
  schoolId: string;
  schoolName: string;
  studentsCount: number;
  costPerStudentUsd: number;
  tuitionPerStudentUsd: number;
  expectedCostPerStudentUsd: number;
  varianceVsScalePct: number;
  surplusPerStudentUsd: number;
  status: SchoolSizeCostEfficiencyStatus;
  z: number;
};

function findScatterPointPayload(
  payload: TooltipProps<number, string>['payload']
): ScatterChartPoint | null {
  if (!payload?.length) return null;
  for (const item of payload) {
    const p = item.payload as Partial<ScatterChartPoint> | undefined;
    if (p?.schoolId && p.schoolName) return p as ScatterChartPoint;
  }
  return null;
}

function ScaleScatterTooltipContent({
  active,
  payload,
}: TooltipProps<number, string>) {
  if (!active) return null;
  const p = findScatterPointPayload(payload);
  if (!p) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-md">
      <p className="font-semibold text-slate-900">{p.schoolName}</p>
      <p className="mt-1 text-slate-600">Students: {fmtInt(p.studentsCount)}</p>
      <p>Cost / student: {fmtUsd(p.costPerStudentUsd)}</p>
      <p>Tuition / student: {fmtUsd(p.tuitionPerStudentUsd)}</p>
      <p>Surplus / student: {fmtUsd(p.surplusPerStudentUsd)}</p>
      <p>Expected for size: {fmtUsd(p.expectedCostPerStudentUsd)}</p>
      <p>vs scale: {fmtPct(p.varianceVsScalePct)}</p>
      <p className="mt-1 font-medium text-slate-700">{efficiencyStatusLabel(p.status)}</p>
    </div>
  );
}

function scatterDotShape(props: unknown) {
  const { cx, cy, fill } = props as { cx?: number; cy?: number; fill?: string };
  if (cx == null || cy == null) return <g />;
  return <circle cx={cx} cy={cy} r={5} fill={fill} fillOpacity={0.85} />;
}

function scatterActiveShape(props: unknown) {
  const { cx, cy, fill } = props as { cx?: number; cy?: number; fill?: string };
  if (cx == null || cy == null) return <g />;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={8}
      fill={fill}
      fillOpacity={0.92}
      stroke="#fff"
      strokeWidth={1.5}
    />
  );
}

function formatDistributionLine(d: SchoolSizeCostDistribution): string {
  return `Cost/student in scope: min ${fmtUsd(d.min)} · p25 ${fmtUsd(d.p25)} · median ${fmtUsd(d.median)} · p75 ${fmtUsd(d.p75)} · max ${fmtUsd(d.max)}`;
}

function ScaleScatterChart({
  rows,
  trendLine,
  distribution,
  loading,
  truncated,
}: {
  rows: SchoolSizeCostSchoolRow[];
  trendLine: { studentsCount: number; expectedCostPerStudentUsd: number }[];
  distribution?: SchoolSizeCostDistribution;
  loading: boolean;
  truncated: boolean;
}) {
  const scatterData = useMemo<ScatterChartPoint[]>(
    () =>
      rows.map((r) => ({
        schoolId: r.schoolId,
        studentsCount: r.studentsCount,
        costPerStudentUsd: r.costPerStudentUsd,
        tuitionPerStudentUsd: r.tuitionPerStudentUsd,
        expectedCostPerStudentUsd: r.expectedCostPerStudentUsd,
        varianceVsScalePct: r.varianceVsScalePct,
        surplusPerStudentUsd: r.surplusPerStudentUsd,
        schoolName: r.schoolName,
        status: r.efficiencyStatus,
        z: Math.max(40, Math.min(200, r.tuitionPerStudentUsd / 15)),
      })),
    [rows]
  );

  const lineData = useMemo(
    () =>
      [...trendLine].sort((a, b) => a.studentsCount - b.studentsCount).map((p) => ({
        studentsCount: p.studentsCount,
        expectedCostPerStudentUsd: p.expectedCostPerStudentUsd,
      })),
    [trendLine]
  );

  if (loading) {
    return (
      <div className="h-[360px] animate-pulse rounded-xl border border-slate-200 bg-slate-100" aria-hidden />
    );
  }

  if (scatterData.length === 0) {
    return (
      <div className="flex h-[240px] items-center justify-center rounded-xl border border-dashed border-slate-200 text-sm text-slate-500">
        No schools to chart.
      </div>
    );
  }

  return (
    <div>
      <div className="h-[min(420px,55vh)] w-full min-w-0 overflow-visible">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 16, right: 24, bottom: 12, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              type="number"
              dataKey="studentsCount"
              name="Students"
              tick={{ fontSize: 11 }}
              label={{ value: 'Enrollment (students)', position: 'insideBottom', offset: -4, fontSize: 11 }}
            />
            <YAxis
              type="number"
              dataKey="costPerStudentUsd"
              name="Cost"
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => {
                const n = Number(v);
                if (n >= 1000) return `$${Math.round(n / 100) / 10}k`;
                return `$${Math.round(n)}`;
              }}
              label={{
                value: 'Cost / student (USD)',
                angle: -90,
                position: 'insideLeft',
                fontSize: 11,
              }}
            />
            <ZAxis type="number" dataKey="z" range={[40, 200]} />
            <RechartsTooltip
              shared={false}
              cursor={{ strokeDasharray: '3 3' }}
              wrapperStyle={{ zIndex: 30, pointerEvents: 'none' }}
              content={<ScaleScatterTooltipContent />}
            />
            {lineData.length >= 2 ? (
              <Line
                type="monotone"
                data={lineData}
                dataKey="expectedCostPerStudentUsd"
                stroke="#64748b"
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
                style={{ pointerEvents: 'none' }}
                legendType="none"
                name="Expected cost for size (band medians)"
              />
            ) : null}
            <Scatter
              data={scatterData}
              name="Schools"
              fill="#4f46e5"
              shape={scatterDotShape}
              activeShape={scatterActiveShape}
            >
              {scatterData.map((entry) => (
                <Cell key={entry.schoolId} fill={scatterFill(entry.status)} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      {distribution ? (
        <p className="mt-2 text-center text-xs text-slate-500">{formatDistributionLine(distribution)}</p>
      ) : null}
      {truncated ? (
        <p className="mt-2 text-center text-xs text-slate-500">
          Showing {CHART_MAX_POINTS} schools on the chart. Use the schools table for the full list.
        </p>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-4 text-xs text-slate-600">
        <span className="flex items-center gap-1.5">
          <span className="inline-block size-3 rounded-full bg-[#d97706]" aria-hidden /> High cost for size
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block size-3 rounded-full bg-[#059669]" aria-hidden /> Low cost for size
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block size-3 rounded-full bg-[#4f46e5]" aria-hidden /> Efficient
        </span>
        <span className="text-slate-500">Dashed line = expected cost for size (band medians)</span>
      </div>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="space-y-2 rounded-xl border border-slate-200/90 bg-white p-4" aria-hidden>
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="h-10 animate-pulse rounded bg-slate-100" />
      ))}
    </div>
  );
}

export function SchoolSizeCostAnalysisPage() {
  const reportYearsQuery = useBudgetReportYearsListQuery();
  const defaultYear = useDefaultYear();

  const [year, setYear] = useState('');
  const [governorateId, setGovernorateId] = useState(ANY_VALUE);
  const [cityId, setCityId] = useState(ANY_VALUE);
  const [areaId, setAreaId] = useState(ANY_VALUE);
  const [sizeBandId, setSizeBandId] = useState(ANY_VALUE);
  const [viewMode, setViewMode] = useState<SchoolSizeCostViewMode>('chart');
  const [rowFilter, setRowFilter] = useState<SchoolSizeCostRowFilter>('all');
  const [tableSearch, setTableSearch] = useState('');
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState<number>(TABLE_PAGE_SIZE_OPTIONS[1]);
  const [applied, setApplied] = useState<SchoolSizeCostFilterParams | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('varianceVsScalePct');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    if (!year && defaultYear) setYear(defaultYear);
  }, [defaultYear, year]);

  const governorateIds = useMemo(
    () => (governorateId.trim() ? [governorateId.trim()] : []),
    [governorateId]
  );
  const cityIds = useMemo(() => (cityId.trim() ? [cityId.trim()] : []), [cityId]);

  const governoratesQuery = useGovernoratesQuery();
  const citiesQuery = useCitiesForGovernoratesQuery(governorateIds);
  const areasQuery = useAreasForCitiesQuery(cityIds, Boolean(year.trim() && governorateIds.length > 0));
  const schoolBandsQuery = useSchoolSizeCostSchoolBandsQuery(year);

  useEffect(() => {
    if (!governorateId.trim()) {
      setCityId(ANY_VALUE);
      setAreaId(ANY_VALUE);
    }
  }, [governorateId]);

  useEffect(() => {
    if (!cityId.trim()) setAreaId(ANY_VALUE);
  }, [cityId]);

  const governorateOptions = useMemo(() => {
    const items = (governoratesQuery.data ?? []).map((g) => ({
      value: String(g.id),
      label: g.name,
    }));
    return withAnyOption(items);
  }, [governoratesQuery.data]);

  const cityOptions = useMemo(() => {
    const items = (citiesQuery.data ?? []).map((c) => ({
      value: String(c.id),
      label: c.name,
    }));
    return [{ value: ANY_VALUE, label: 'All districts' }, ...items];
  }, [citiesQuery.data]);

  const areaOptions = useMemo(() => {
    const items = (areasQuery.data ?? []).map((a) => ({
      value: String(a.id),
      label: a.name,
    }));
    return [{ value: ANY_VALUE, label: 'All areas' }, ...items];
  }, [areasQuery.data]);

  const sizeBandOptions = useMemo(() => {
    const items = (schoolBandsQuery.data?.items ?? []).map((b) => ({
      value: b.id,
      label: bandFullName(b, 'students'),
    }));
    return [{ value: ANY_VALUE, label: 'All size bands' }, ...items.filter((o) => o.value.trim())];
  }, [schoolBandsQuery.data?.items]);

  const yearOptions = useMemo(() => {
    const raw = reportYearsQuery.data ?? [];
    return [...raw].sort((a, b) => b - a).map((n) => ({ value: String(n), label: String(n) }));
  }, [reportYearsQuery.data]);

  const searchQuery = useSchoolSizeCostQuery(applied, applied != null);

  const resolveFilterParams = useCallback((): SchoolSizeCostFilterParams | null => {
    if (!year.trim()) return null;
    const gov = governoratesQuery.data?.find((g) => String(g.id) === governorateId.trim());
    const city = citiesQuery.data?.find((c) => String(c.id) === cityId.trim());
    const area = areasQuery.data?.find((a) => String(a.id) === areaId.trim());
    const band = schoolBandsQuery.data?.items.find((b) => b.id === sizeBandId.trim());

    const params: SchoolSizeCostFilterParams = { year: year.trim() };
    if (gov) {
      params.governorateId = gov.id;
      params.governorateName = gov.name;
    }
    if (city) {
      params.cityId = city.id;
      params.cityName = city.name;
    }
    if (area) {
      params.areaId = area.id;
      params.areaName = area.name;
    }
    if (band) {
      params.schoolSizeBandId = band.id;
      params.schoolSizeBandName = bandFullName(band, 'students');
    }
    return params;
  }, [
    year,
    governorateId,
    cityId,
    areaId,
    sizeBandId,
    governoratesQuery.data,
    citiesQuery.data,
    areasQuery.data,
    schoolBandsQuery.data?.items,
  ]);

  const handleSearch = useCallback(() => {
    if (!year.trim()) {
      message.warning('Select a year.');
      return;
    }
    const params = resolveFilterParams();
    if (!params) return;
    setApplied(params);
    setRowFilter('all');
    setTableSearch('');
    setTablePage(1);
    setSortKey('varianceVsScalePct');
    setSortDir('desc');
  }, [year, resolveFilterParams]);

  const rows = searchQuery.data?.rows ?? [];
  const bandSummaries = searchQuery.data?.bandSummaries ?? [];
  const trendLine = searchQuery.data?.trendLine ?? [];
  const scaleInsight = searchQuery.data?.scaleInsight ?? '';
  const costDistribution = searchQuery.data?.distribution;
  const summary = searchQuery.data?.summary;
  const isLoading = Boolean(applied && searchQuery.isFetching);

  const filteredRows = useMemo(() => {
    let list = rows;
    if (rowFilter !== 'all') {
      list = list.filter((r) => r.efficiencyStatus === rowFilter);
    }
    const q = tableSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (r) =>
        r.schoolName.toLowerCase().includes(q) ||
        r.schoolCode.toLowerCase().includes(q)
    );
  }, [rows, rowFilter, tableSearch]);

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      const m = sortDir === 'asc' ? 1 : -1;
      if (sortKey === 'schoolName') {
        return a.schoolName.localeCompare(b.schoolName, undefined, { sensitivity: 'base' }) * m;
      }
      return (a[sortKey] - b[sortKey]) * m;
    });
  }, [filteredRows, sortKey, sortDir]);

  const chartRows = useMemo(() => sortedRows.slice(0, CHART_MAX_POINTS), [sortedRows]);
  const chartTruncated = sortedRows.length > CHART_MAX_POINTS;

  const totalTableRows = sortedRows.length;
  const totalTablePages = Math.max(1, Math.ceil(totalTableRows / tablePageSize) || 1);

  useEffect(() => {
    setTablePage(1);
  }, [rowFilter, tableSearch, applied]);

  useEffect(() => {
    if (tablePage > totalTablePages) setTablePage(totalTablePages);
  }, [tablePage, totalTablePages]);

  const paginatedRows = useMemo(() => {
    const start = (tablePage - 1) * tablePageSize;
    return sortedRows.slice(start, start + tablePageSize);
  }, [sortedRows, tablePage, tablePageSize]);

  const emptyTableMessage = useMemo(() => {
    if (rows.length === 0) return 'No schools match these filters.';
    if (tableSearch.trim()) return 'No schools match this search.';
    if (rowFilter !== 'all') return `No schools with status "${efficiencyStatusLabel(rowFilter)}".`;
    return 'No schools to show.';
  }, [rows.length, tableSearch, rowFilter]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'schoolName' ? 'asc' : 'desc');
    }
  };

  const sortIndicator = (key: SortKey) => (sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  const scopeLabel = useMemo(() => {
    if (!applied) return '';
    const parts = [applied.year];
    if (applied.governorateName) parts.push(applied.governorateName);
    if (applied.cityName) parts.push(applied.cityName);
    if (applied.areaName) parts.push(applied.areaName);
    if (applied.schoolSizeBandName) parts.push(applied.schoolSizeBandName);
    return parts.join(' · ');
  }, [applied]);

  const handleExportExcel = async () => {
    if (sortedRows.length === 0) {
      message.warning('No rows to export.');
      return;
    }
    try {
      await exportSchoolSizeCostExcel(sortedRows, schoolSizeCostExportBasename(year));
      message.success('Downloaded.');
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Export failed.');
    }
  };

  const handleExportPdf = async () => {
    if (sortedRows.length === 0) {
      message.warning('No rows to export.');
      return;
    }
    try {
      await exportSchoolSizeCostPdf(sortedRows, schoolSizeCostExportBasename(year), [
        `School Size vs Cost — ${scopeLabel}`,
      ]);
      message.success('Downloaded.');
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Export failed.');
    }
  };

  const openSchoolProfile = (row: SchoolSizeCostSchoolRow) => {
    window.open(
      schoolProfileHref({
        schoolId: row.schoolId,
        year: applied?.year ?? year,
        governorateId: row.governorateId,
      }),
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <div className="space-y-6 pb-10">
      <PageHeader
        title="School Size vs Cost"
        description="See how cost per student and tuition relate to school size — economies of scale and schools that are costly for their enrollment. Uses size-band expectations, not peer-group medians (see Group Comparisons for peer outliers)."
      />

      <div className="rounded-xl border border-slate-200/90 bg-white p-5 shadow-sm ring-1 ring-slate-100">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[8rem]">
            <label className="mb-1 block text-xs font-semibold text-slate-700">Year</label>
            <Select
              className="w-full min-w-[8rem]"
              placeholder="Year"
              value={year || undefined}
              onChange={setYear}
              options={yearOptions}
              loading={reportYearsQuery.isPending}
            />
          </div>
          <div className="min-w-[10rem]">
            <label className="mb-1 block text-xs font-semibold text-slate-700">Governorate</label>
            <Select
              className="w-full min-w-[10rem]"
              value={governorateId || ANY_VALUE}
              onChange={setGovernorateId}
              options={governorateOptions}
              loading={governoratesQuery.isPending}
            />
          </div>
          <div className="min-w-[10rem]">
            <label className="mb-1 block text-xs font-semibold text-slate-700">District (city)</label>
            <Select
              className="w-full"
              value={cityId || ANY_VALUE}
              onChange={setCityId}
              options={cityOptions}
              loading={citiesQuery.isLoading}
              disabled={!governorateId.trim()}
            />
          </div>
          <div className="min-w-[10rem]">
            <label className="mb-1 block text-xs font-semibold text-slate-700">Area</label>
            <Select
              className="w-full"
              value={areaId || ANY_VALUE}
              onChange={setAreaId}
              options={areaOptions}
              loading={areasQuery.isLoading}
              disabled={!cityId.trim()}
            />
          </div>
          <div className="min-w-[12rem]">
            <label className="mb-1 block text-xs font-semibold text-slate-700">School size band</label>
            <Select
              className="w-full min-w-[12rem]"
              value={sizeBandId || ANY_VALUE}
              onChange={setSizeBandId}
              options={sizeBandOptions}
              loading={schoolBandsQuery.isPending}
              disabled={!year.trim()}
            />
          </div>
          <Button type="button" variant="primary" className="px-5 py-2 text-sm" onClick={handleSearch}>
            <Search className="mr-1.5 inline size-4" aria-hidden />
            Search
          </Button>
        </div>
      </div>

      {!applied ? (
        <section className="rounded-2xl border border-indigo-200/60 bg-gradient-to-br from-indigo-50/40 via-white to-slate-50/90 px-6 py-12 shadow-md ring-1 ring-indigo-100/80 sm:px-10 sm:py-14">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200/80">
              <TrendingUp className="size-7" strokeWidth={1.5} aria-hidden />
            </div>
            <h2 className="mt-6 text-xl font-bold tracking-tight text-slate-900">
              Analyze cost and tuition by enrollment
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Select a reporting year and optional region or size band, then search to explore economies of
              scale and schools that cost more than expected for their size.
            </p>
          </div>
        </section>
      ) : null}

      {applied && searchQuery.isError ? (
        <Alert type="error" showIcon message="Failed to load school size vs cost data." />
      ) : null}

      {applied && !searchQuery.isError ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <KpiCard label="Schools in scope" value={isLoading ? '…' : fmtInt(summary?.schoolCount ?? 0)} icon={Users} />
            <KpiCard
              label="Median cost / student"
              value={isLoading ? '…' : fmtUsd(summary?.medianCostPerStudentUsd ?? 0)}
              icon={DollarSign}
            />
            <KpiCard
              label="Median tuition / student"
              value={isLoading ? '…' : fmtUsd(summary?.medianTuitionPerStudentUsd ?? 0)}
              icon={DollarSign}
            />
            <KpiCard
              label="High cost for size"
              value={isLoading ? '…' : fmtInt(summary?.highCostForSizeCount ?? 0)}
              icon={TrendingUp}
              detail={`More than ${SCALE_VARIANCE_THRESHOLD_PCT}% above band expected cost`}
            />
            <KpiCard
              label="Median enrollment"
              value={isLoading ? '…' : fmtInt(summary?.medianEnrollment ?? 0)}
              icon={Users}
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-600">
              <span className="font-medium text-slate-800">{scopeLabel}</span>
            </p>
            <Segmented
              value={viewMode}
              onChange={(v) => setViewMode(v as SchoolSizeCostViewMode)}
              options={[
                { label: 'Chart', value: 'chart' },
                { label: 'By size band', value: 'bands' },
                { label: 'Schools table', value: 'table' },
              ]}
            />
          </div>

          {rows.length === 0 && !isLoading ? (
            <Alert
              type="info"
              showIcon
              message="No schools match these filters."
              description="Try broadening the region or clearing the size band filter."
            />
          ) : null}

          {viewMode === 'chart' && rows.length > 0 ? (
            <section className="rounded-xl border border-slate-200/90 bg-white p-5 shadow-sm ring-1 ring-slate-100">
              <h3 className="text-sm font-semibold text-slate-800">Enrollment vs cost per student</h3>
              <p className="mt-1 text-xs text-slate-500">{scaleInsight}</p>
              <div className="mt-4">
                <ScaleScatterChart
                  rows={chartRows}
                  trendLine={trendLine}
                  distribution={costDistribution}
                  loading={isLoading}
                  truncated={chartTruncated}
                />
              </div>
            </section>
          ) : null}

          {viewMode === 'bands' ? (
            <section className="overflow-x-auto rounded-xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-100">
              {isLoading ? (
                <TableSkeleton />
              ) : bandSummaries.length === 0 ? (
                <p className="p-6 text-center text-sm text-slate-500">No band summary data.</p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/90 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      <th className="px-4 py-3">Size band</th>
                      <th className="px-3 py-3">Schools</th>
                      <th className="px-3 py-3">Median students</th>
                      <th className="px-3 py-3">Median cost/student</th>
                      <th className="px-3 py-3">Median tuition/student</th>
                      <th className="px-3 py-3">Median surplus</th>
                      <th className="px-3 py-3">vs national band</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bandSummaries.map((b) => (
                      <tr key={b.schoolSizeBandId} className="border-b border-slate-100 last:border-0">
                        <td className="px-4 py-2.5 font-medium text-slate-800">{b.schoolSizeBandName}</td>
                        <td className="px-3 py-2.5 font-mono tabular-nums">{fmtInt(b.schoolsCount)}</td>
                        <td className="px-3 py-2.5 font-mono tabular-nums">{fmtInt(b.medianStudents)}</td>
                        <td className="px-3 py-2.5 font-mono tabular-nums">{fmtUsd(b.medianCostPerStudentUsd)}</td>
                        <td className="px-3 py-2.5 font-mono tabular-nums">{fmtUsd(b.medianTuitionPerStudentUsd)}</td>
                        <td className="px-3 py-2.5 font-mono tabular-nums">{fmtUsd(b.medianSurplusPerStudentUsd)}</td>
                        <td className="px-3 py-2.5 font-mono tabular-nums">{fmtPct(b.varianceVsNationalBandPct)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          ) : null}

          {viewMode === 'table' ? (
            <section className="rounded-xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-100">
              <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-200/80 px-4 py-3">
                <div className="flex flex-wrap items-end gap-3">
                  <Segmented
                    size="small"
                    value={rowFilter}
                    onChange={(v) => setRowFilter(v as SchoolSizeCostRowFilter)}
                    options={[
                      { label: 'All', value: 'all' },
                      { label: 'High cost', value: 'high_cost_for_size' },
                      { label: 'Low cost', value: 'low_cost_for_size' },
                      { label: 'Efficient', value: 'efficient' },
                    ]}
                  />
                  <div>
                    <label htmlFor="ssc-table-search" className="mb-1 block text-xs font-semibold text-slate-700">
                      Search schools
                    </label>
                    <Input
                      id="ssc-table-search"
                      allowClear
                      prefix={<Search className="size-4 text-slate-400" aria-hidden />}
                      placeholder="Name or code"
                      value={tableSearch}
                      onChange={(e) => setTableSearch(e.target.value)}
                      className="w-full min-w-[14rem] sm:w-56"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pb-0.5">
                  <Button type="button" variant="secondary" className="text-xs" onClick={handleExportExcel}>
                    <FileSpreadsheet className="mr-1 inline size-3.5" aria-hidden />
                    Excel
                  </Button>
                  <Button type="button" variant="secondary" className="text-xs" onClick={handleExportPdf}>
                    <FileText className="mr-1 inline size-3.5" aria-hidden />
                    PDF
                  </Button>
                </div>
              </div>

              {isLoading ? (
                <TableSkeleton />
              ) : sortedRows.length === 0 ? (
                <p className="p-6 text-center text-sm text-slate-500">{emptyTableMessage}</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50/90 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                          <th className="px-4 py-3">
                            <button type="button" className="hover:text-indigo-700" onClick={() => toggleSort('schoolName')}>
                              School{sortIndicator('schoolName')}
                            </button>
                          </th>
                          <th className="px-3 py-3">Code</th>
                          <th className="px-3 py-3">Governorate</th>
                          <th className="px-3 py-3">District</th>
                          <th className="px-3 py-3">
                            <button type="button" className="hover:text-indigo-700" onClick={() => toggleSort('studentsCount')}>
                              Students{sortIndicator('studentsCount')}
                            </button>
                          </th>
                          <th className="px-3 py-3">Size band</th>
                          <th className="px-3 py-3">
                            <button type="button" className="hover:text-indigo-700" onClick={() => toggleSort('costPerStudentUsd')}>
                              Cost{sortIndicator('costPerStudentUsd')}
                            </button>
                          </th>
                          <th className="px-3 py-3">
                            <button type="button" className="hover:text-indigo-700" onClick={() => toggleSort('tuitionPerStudentUsd')}>
                              Tuition{sortIndicator('tuitionPerStudentUsd')}
                            </button>
                          </th>
                          <th className="px-3 py-3">Surplus</th>
                          <th className="px-3 py-3">Expected</th>
                          <th className="px-3 py-3">
                            <button type="button" className="hover:text-indigo-700" onClick={() => toggleSort('varianceVsScalePct')}>
                              vs Scale{sortIndicator('varianceVsScalePct')}
                            </button>
                          </th>
                          <th className="px-3 py-3">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedRows.map((row) => (
                          <tr key={row.schoolId} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                            <td className="max-w-[14rem] px-4 py-2.5">
                              <button
                                type="button"
                                onClick={() => openSchoolProfile(row)}
                                className="truncate text-left font-medium text-indigo-700 hover:underline"
                                title={row.schoolName}
                              >
                                {row.schoolName}
                              </button>
                            </td>
                            <td className="px-3 py-2.5 font-mono text-xs text-slate-600">{row.schoolCode}</td>
                            <td className="px-3 py-2.5 text-slate-700">{row.governorateName}</td>
                            <td className="px-3 py-2.5 text-slate-600">
                              {row.cityName}
                              {row.areaName ? ` · ${row.areaName}` : ''}
                            </td>
                            <td className="px-3 py-2.5 font-mono tabular-nums">{fmtInt(row.studentsCount)}</td>
                            <td className="max-w-[10rem] px-3 py-2.5 text-xs text-slate-600" title={row.schoolSizeBandName}>
                              {row.schoolSizeBandName}
                            </td>
                            <td className="px-3 py-2.5 font-mono tabular-nums font-medium">{fmtUsd(row.costPerStudentUsd)}</td>
                            <td className="px-3 py-2.5 font-mono tabular-nums">{fmtUsd(row.tuitionPerStudentUsd)}</td>
                            <td className="px-3 py-2.5 font-mono tabular-nums">{fmtUsd(row.surplusPerStudentUsd)}</td>
                            <td className="px-3 py-2.5 font-mono tabular-nums text-slate-600">{fmtUsd(row.expectedCostPerStudentUsd)}</td>
                            <td className="px-3 py-2.5 font-mono tabular-nums">{fmtPct(row.varianceVsScalePct)}</td>
                            <td className="px-3 py-2.5">
                              <span
                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${statusBadgeClass(row.efficiencyStatus)}`}
                              >
                                {efficiencyStatusLabel(row.efficiencyStatus)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs font-medium tabular-nums text-slate-600">
                      Showing{' '}
                      <span className="font-semibold text-slate-900">
                        {(tablePage - 1) * tablePageSize + 1}-
                        {Math.min(tablePage * tablePageSize, totalTableRows)}
                      </span>{' '}
                      of <span className="font-semibold text-slate-900">{totalTableRows}</span>
                      {tableSearch.trim() || rowFilter !== 'all' ? ' (filtered)' : ''}
                    </p>
                    <Pagination
                      size="small"
                      current={tablePage}
                      pageSize={tablePageSize}
                      total={totalTableRows}
                      showSizeChanger
                      pageSizeOptions={[...TABLE_PAGE_SIZE_OPTIONS]}
                      onChange={(p, ps) => {
                        const nextSize = ps ?? tablePageSize;
                        if (nextSize !== tablePageSize) {
                          setTablePageSize(nextSize);
                          setTablePage(1);
                        } else {
                          setTablePage(p);
                        }
                      }}
                    />
                  </div>
                </>
              )}
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
